<template>
  <div
    :data-document-id="doc.id"
    :id="doc.id + ''"
    class="border border-grey-925 flex items-center pl-2 pr-1 py-1 bg-white shadow-inner relative"
  >
    <OhVueIcon :name="getIcon(props.doc)" class="mr-1 mt-1v text-blue-france-sun-113" scale="1.5" />
    <PTooltip label="Signature prépositionnée" :offset="5" :delay="500">
      <template #label="{ label }">
        <div class="whitespace-nowrap">{{ label }}</div>
      </template>
      <div
        v-if="doc.signaturePositionsCount"
        class="rounded-full p-1 mr-1 flex"
        :class="!canEdit || isLetterFileArchived ? 'bg-grey-850 text-grey-200' : 'bg-green-tilleul-verveine-925'"
      >
        <span class="text-sm">{{ doc.signaturePositionsCount }}</span>
        <OhVueIcon scale="1" :name="postItIcon" class="mt-1v" />
      </div>
    </PTooltip>
    <PTooltip v-if="doc.signed && !doc.pades" label="Document signé" :delay="500">
      <div
        class="absolute -left-1v -top-1v bg-white rounded-full outline outline-1 p-1"
        :class="!canEdit || isLetterFileArchived ? 'text-grey-425' : 'text-blue-france-sun-113'"
      >
        <OhVueIcon scale="0.9" class="absolute top-0 left-0" :name="signIcon" />
      </div>
    </PTooltip>
    <PTooltip v-if="doc.signed && doc.pades" label="Document signé par carte agent" :delay="500">
      <div
        class="absolute -left-1v -top-1v rounded-full outline outline-1 p-[0.6rem] text-white"
        :class="!canEdit || isLetterFileArchived ? 'bg-grey-425' : 'bg-blue-france-sun-113'"
      >
        <OhVueIcon scale="0.8" class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" :name="padesIcon" />
      </div>
    </PTooltip>
    <div class="flex items-center overflow-hidden">
      <NameInput
        v-show="rename"
        :initial-value="doc.name"
        :readonly="readonly || !canEdit"
        ref="nameInput"
        @submit="updateName(doc, $event)"
        type="document"
        :id="doc.id"
        hide-edit-btn
        hide-save-status
        :data-sentry-id="`DocNameInput-${doc.id}`"
      />
      <PTooltip
        v-show="!rename"
        :label="tooltipLabel"
        activator-wrapper-classes="text-left overflow-hidden text-ellipsis whitespace-nowrap"
        class="w-full"
        :delay="500"
        :offset="10"
      >
        <button
          :data-sentry-id="`ViewDoc-${doc.id}`"
          @click="viewDoc"
          :class="{
            'text-grey-425': !canEdit || isLetterFileArchived,
          }"
          :disabled="!isViewable"
        >
          {{ docName }}
        </button>
      </PTooltip>
    </div>
    <PButton
      v-if="!readonly && canEdit && !showSaved"
      size="icon"
      mode="tertiary-borderless"
      @click="renameDoc()"
      aria-label="Modifier le nom du document"
      class="ml-1"
      :data-sentry-id="`DocChangeNameBtn-${doc.id}`"
    >
      <OhVueIcon :name="editIcon" class="text-blue-france-sun-113" />
    </PButton>

    <SaveFieldStatus v-if="showSaved" :loading="asyncOperation.loading.value" scale="1.2" class="ml-2" load-size="sm" />
    <div class="ml-auto flex items-center">
      <PButton
        v-if="!readonly && canEdit && !(isFirst && isFirstFolder)"
        @click="emit('move', 'up')"
        size="icon"
        mode="tertiary-borderless"
        :aria-label="isFirst ? `déplacer ${docName} vers le répertoire précédent` : `déplacer vers le haut ${docName}`"
        class="sr-only focus:not-sr-only"
        :data-sentry-id="`DocMoveUpBtn-${doc.id}`"
      >
        <OhVueIcon :name="upIcon" scale="2" />
      </PButton>
      <PButton
        v-if="!readonly && canEdit && !(isLast && isLastFolder)"
        @click="emit('move', 'down')"
        size="icon"
        mode="tertiary-borderless"
        :aria-label="isLast ? `déplacer ${docName} vers le répertoire suivant` : `déplacer vers le bas ${docName}`"
        class="sr-only focus:not-sr-only"
        :data-sentry-id="`DocMoveDownBtn-${doc.id}`"
      >
        <OhVueIcon :name="downIcon" scale="2" />
      </PButton>
      <OhVueIcon
        v-if="!readonly && canEdit"
        :name="dragIcon"
        scale="1.2"
        class="mr-1v drag-handle cursor-move"
        :class="{ 'text-blue-france-sun-113': !isDefault }"
      />
      <DocumentVersions
        v-if="(doc.documentVersions?.length || 0) > 1"
        :document="doc"
        v-model="isDocVersionsModalOpened"
      />
      <FileLinkLoader v-if="isDefault" type="document" :id="doc.id" v-model="isFileLinkOpened1" />
      <FileLinkLoader v-if="isDefault" type="document" :id="doc.id" mailto v-model="isFileLinkOpened2" />
      <slot name="actions">
        <KebabMenu
          size="sm"
          class="text-sm text-grey-50"
          :class="{ 'ml-auto': readonly }"
          placement="left-start"
          :aria-label="`Action rapide sur le document ${docName}`"
          :disabled="!isDefault && readonly"
        >
          <template #default="{ close }">
            <button
              v-if="isViewable"
              class="hidden lg:block text-start w-full"
              @click="viewDoc"
              :data-sentry-id="`DocSignBtn-${doc.id}`"
            >
              <OhVueIcon :name="viewIcon" scale="0.83" />
              Ouvrir / Signer
            </button>
            <PTooltip
              v-if="isK2000Editable && !readonly"
              label="Action impossible sur un document archivé"
              :disabled="canEdit"
              :delay="500"
              :offset="5"
            >
              <button
                :disabled="!canEdit"
                :class="{
                  'text-grey-425 cursor-not-allowed': !canEdit,
                }"
                class="text-start w-full"
                @click="openEditInK2000(close)"
                :data-sentry-id="`DocEditK2000Btn-${doc.id}`"
              >
                <OhVueIcon :name="editIcon" scale="0.83" />
                Modifier sur le poste
              </button>
            </PTooltip>
            <PTooltip
              v-if="isCollaboraEditable && !readonly"
              label="Action impossible sur un document archivé"
              :disabled="canEdit"
              :delay="500"
              :offset="5"
            >
              <button
                :disabled="!canEdit"
                :class="{
                  'text-grey-425 cursor-not-allowed': !canEdit,
                }"
                class="text-start w-full"
                @click="openEditInCollabora(close)"
                :data-sentry-id="`DocEditCollaboraBtn-${doc.id}`"
              >
                <OhVueIcon :name="editIcon" scale="0.83" />
                Modifier en ligne
              </button>
            </PTooltip>

            <button
              v-if="isDefault"
              @click="isFileLinkOpened1 = true"
              class="text-start"
              data-sentry-id="FileLinkLoaderDlBtn"
            >
              <OhVueIcon :name="downloadIcon" scale="0.83" />
              Télécharger
            </button>
            <button
              v-if="isDefault"
              @click="isFileLinkOpened2 = true"
              class="text-start"
              data-sentry-id="FileLinkLoaderDlBtn"
            >
              <OhVueIcon :name="mailIcon" scale="0.83" />
              Envoyer par mail
            </button>
            <ReplaceDocument v-if="isDefault && !readonly" :disabled="!canEdit" :doc="doc" @done="close" />
            <button
              v-if="(doc.documentVersions?.length || 0) > 1"
              @click="isDocVersionsModalOpened = true"
              class="text-start"
              data-sentry-id="DocVersionsOpenBtn-{{ document.id }}"
            >
              Voir les versions
            </button>
            <button
              class="text-start w-full"
              v-if="isLink"
              @click="copyLink(close)"
              :data-sentry-id="`DocCopyLinkBtn-${doc.id}`"
            >
              Copier le lien
            </button>
            <a
              v-if="isLink"
              :href="`mailto:?body=${encodeURIComponent(mailtoBody)}`"
              :data-sentry-id="`DocSendbymailLink-${doc.id}`"
              >Envoyer par mail</a
            >
            <PTooltip
              v-if="!readonly"
              label="Action impossible sur un document archivé"
              :disabled="canEdit"
              :delay="500"
              :offset="5"
              class=""
            >
              <button
                :disabled="!canEdit"
                :class="{
                  'text-grey-425 cursor-not-allowed': !canEdit,
                  'text-error-425': canEdit,
                }"
                @click="remove(doc.id, close)"
                class="border-t pt-1v border-grey-925 text-start w-full"
                :data-sentry-id="`DocDeleteBtn-${doc.id}`"
              >
                <OhVueIcon :name="deleteIcon" scale="0.83" />
                Supprimer le document
              </button>
            </PTooltip>
          </template>
        </KebabMenu>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FormOperation, type ParapheurConfig } from "@/app/types";
import {
  AlertDuration,
  AlertType,
  PButton,
  PTooltip,
  useAlertManager,
  useConfig,
  useIcons,
  usePastaRouter,
} from "@pasta/box-v2";
import { OhVueIcon } from "oh-vue-icons";
import {
  BiEye,
  FaRegularAddressCard,
  FaSignature,
  MdDraghandle,
  RiArrowDropDownLine,
  RiArrowDropUpLine,
  RiDownloadLine,
  RiEditFill,
  RiMailSendLine,
  RiStickyNoteLine,
} from "oh-vue-icons/icons";
import { RiDeleteBin6Line } from "oh-vue-icons/icons/ri";
import { computed, inject, ref } from "vue";
import DocumentVersions from "../../../components/DocumentVersions.vue";
import FileLinkLoader from "../../../components/FileLinkLoader.vue";
import KebabMenu from "../../../components/KebabMenu.vue";
import NameInput from "../../../components/NameInput.vue";
import SaveFieldStatus from "../../../components/SaveFieldStatus.vue";
import { useAsyncOperation, useNotifyAsyncOperation } from "../../../composable/useAsyncOperation";
import { useFileIcon } from "../../../composable/useFileIcon";
import { type LetterFileDocumentDto, LetterFileDocumentType } from "../../../dto/letter-file-document.dto";
import { useLetterFileDocumentApi } from "../../../http/letter-file-document.api";
import { useWopiStore } from "../../../stores/wopi.store";
import { canEditAfterArchival, type FormProvide, FormToken } from "../utils";
import ReplaceDocument from "./replace-document.vue";

interface Props {
  doc: LetterFileDocumentDto;
  readonly: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isFirstFolder?: boolean;
  isLastFolder?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "move", direction: "up" | "down"): void;
  (e: "edit", id: number): void;
}>();

const router = usePastaRouter();
const alertManager = useAlertManager();
const config = useConfig<ParapheurConfig>();
const isDefault = computed(() => props.doc.type === LetterFileDocumentType.DEFAULT);
const isLink = computed(() => props.doc.type === LetterFileDocumentType.URL);
const mailtoBody = computed(() => {
  if (!isLink.value) return "";
  return `Accédez à ${props.doc.name} en cliquant sur le lien suivant : ${props.doc.url}`;
});
const canEdit = computed(() => canEditAfterArchival(letterFile.value!, props.doc));
const isLetterFileArchived = computed(() => letterFile.value?.archived);

const [
  dragIcon,
  signIcon,
  upIcon,
  downIcon,
  editIcon,
  postItIcon,
  padesIcon,
  viewIcon,
  downloadIcon,
  mailIcon,
  deleteIcon,
] = useIcons(
  MdDraghandle,
  FaSignature,
  RiArrowDropUpLine,
  RiArrowDropDownLine,
  RiEditFill,
  RiStickyNoteLine,
  FaRegularAddressCard,
  BiEye,
  RiDownloadLine,
  RiMailSendLine,
  RiDeleteBin6Line
);
const documentApi = useLetterFileDocumentApi();
const notifyAsyncOperation = useNotifyAsyncOperation();
const { refresh, letterFile } = inject<FormProvide>(FormToken)!;

const docName = computed(() => {
  return props.doc.type === LetterFileDocumentType.DEFAULT ? props.doc.name + props.doc.extension : props.doc.name;
});
const tooltipLabel = computed(() => {
  let label = docName.value;
  if (!isViewable.value) label = "[Non signable] " + label;
  // Tooltip is 200px wide, we add a zero-width space every 24 char so that is breaks line gracefully
  // https://en.wikipedia.org/wiki/Zero-width_space
  return label.replace(/.{22}/g, "$&\u200B").trimEnd();
});

const rename = ref(false);
const isDocVersionsModalOpened = ref(false);
const isFileLinkOpened1 = ref(false);
const isFileLinkOpened2 = ref(false);

const deleting = ref(false);

async function remove(id: number, close: () => void) {
  const message =
    props.doc.type === LetterFileDocumentType.DEFAULT
      ? "Êtes vous sûr(e) de vouloir supprimer ce document et toutes ses versions ?"
      : "Êtes vous sûr(e) de vouloir supprimer ce lien ?";
  if (confirm(message)) {
    deleting.value = true;
    try {
      await notifyAsyncOperation(async () => documentApi.trash(id, letterFile.value!.id), FormOperation.DOCUMENT);
      await notifyAsyncOperation(refresh, FormOperation.REFRESH);
    } finally {
      deleting.value = false;
    }
  }
  close();
}

const nameInput = ref<any>(null);

function renameDoc() {
  rename.value = true;
  const theInput = nameInput.value;
  theInput.$el.querySelector(".name-input-display").click();
}

async function updateName(doc: LetterFileDocumentDto, name: string) {
  if (!name) return;
  if (doc.name !== name) {
    doc.name = name; // optimist
    try {
      await notifyAsyncOperation(
        async () => documentApi.update(doc.id, letterFile.value!.id, { name }),
        `${FormOperation.DOCUMENT_NAME}-${doc.id}`
      );
    } catch {
      alertManager.createAlert({
        message: "Le document n'a pas pu être renommé",
        duration: AlertDuration.MEDIUM,
        type: AlertType.ERROR,
      });
    } finally {
      await notifyAsyncOperation(refresh, FormOperation.REFRESH);
    }
  }
  rename.value = false;
}

const getIcon = useFileIcon();

function copyLink(close: Function) {
  alertManager.createAlert({
    type: AlertType.INFO,
    duration: AlertDuration.MEDIUM,
    message: "Lien copié !",
  });
  navigator.clipboard.writeText(props.doc.url!);
  close();
}

// Sign
const isViewable = computed(() => {
  if (!isDefault.value) return false;

  return (
    config.allowedConversionFormats.includes(props.doc.extension.split(".").pop()!) || props.doc.extension === ".pdf"
  );
});

function viewDoc() {
  router.push({
    name: "doc-sign",
    params: { chrono: letterFile.value!.chronoNumber, docId: props.doc.id },
  });
}

// Edit
const wopiStore = useWopiStore();
const isCollaboraEditable = computed(() => {
  return (
    wopiStore.collaboraEnabled && Object.keys(wopiStore.collaboraEditables).includes(props.doc.extension?.slice(1))
  );
});

const isK2000Editable = computed(() => {
  return wopiStore.k2000Enabled && Object.keys(wopiStore.k2000Editables).includes(props.doc.extension?.slice(1));
});

async function openEditInCollabora(close: () => void) {
  await wopiStore.editWithCollabora(props.doc);
  emit("edit", props.doc.id);
  close();
}

async function openEditInK2000(close: () => void) {
  await wopiStore.editWithK2000(props.doc);
  close();
}

// save status
const asyncOperation = useAsyncOperation();
const showSaved = asyncOperation.getShowSaved(`${FormOperation.DOCUMENT_NAME}-${props.doc.id}`);
</script>
